import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
    RiShoppingCart2Line,
    RiDeleteBin6Line,
    RiMapPinLine,
    RiAddLine,
    RiArrowRightLine,
    RiInformationLine,
} from 'react-icons/ri'
import { useCartStore, selectItemsTotal, selectTotal } from '@/stores/useCartStore'
import { useAddresses } from '@/api/useAddresses'
import { useShippingConfig } from '@/api/useShippingConfig'
import { useModalStore } from '@/stores/useModalStore'
import { AddressFormModal } from '@/components/orders/AddressFormModal'
import { Button, Spinner } from '@/components/ui'
import { ROUTES } from '@/constants/routes'
import type { CartItem } from '@/stores/useCartStore'
import styles from './Cart.module.css'

function CartItemRow({ item }: { item: CartItem }) {
    const updateItemQuantity = useCartStore((s) => s.updateItemQuantity)
    const removeItem = useCartStore((s) => s.removeItem)

    return (
        <div className={styles.itemCard}>
            {/* Thumbnail */}
            <div className={styles.itemThumb}>
                {item.frontPageThumbnail ? (
                    <img src={item.frontPageThumbnail} alt={item.projectName} className={styles.thumbImg} />
                ) : (
                    <div className={styles.thumbPlaceholder}>
                        <RiInformationLine />
                    </div>
                )}
            </div>

            {/* Info */}
            <div className={styles.itemInfo}>
                <p className={styles.itemName}>{item.projectName}</p>
                <p className={styles.itemMeta}>{item.materialName} · {item.sizeName}</p>
                <p className={styles.itemPrice}>S/ {item.pricePerMillar.toFixed(2)} / millar</p>
            </div>

            {/* Quantity + subtotal */}
            <div className={styles.itemControls}>
                <div className={styles.qtyWrapper}>
                    <label className={styles.qtyLabel}>Millares</label>
                    <input
                        type="number"
                        min={1}
                        value={item.quantityMillares}
                        className={styles.qtyInput}
                        onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value, 10) || 1)
                            updateItemQuantity(item.id, val)
                        }}
                    />
                </div>
                <p className={styles.itemSubtotal}>S/ {item.subtotal.toFixed(2)}</p>
            </div>

            {/* Delete */}
            <button
                className={styles.deleteBtn}
                onClick={() => removeItem(item.id)}
                aria-label="Eliminar del carrito"
            >
                <RiDeleteBin6Line />
            </button>
        </div>
    )
}

export function Cart() {
    const navigate = useNavigate()
    const items = useCartStore((s) => s.items)
    const shippingAddressId = useCartStore((s) => s.shippingAddressId)
    const shippingCost = useCartStore((s) => s.shippingCost)
    const notes = useCartStore((s) => s.notes)
    const setShippingAddress = useCartStore((s) => s.setShippingAddress)
    const setNotes = useCartStore((s) => s.setNotes)
    const itemsTotal = useCartStore(selectItemsTotal)
    const total = useCartStore(selectTotal)

    const { data: addresses, isLoading: loadingAddresses } = useAddresses()
    const { data: shippingConfigs } = useShippingConfig()
    const openModal = useModalStore((s) => s.openModal)

    // Auto-select default address on load
    useEffect(() => {
        if (addresses && addresses.length > 0 && !shippingAddressId) {
            const defaultAddr = addresses.find((a) => a.is_default) || addresses[0]
            const config = shippingConfigs?.find((s) => s.department === defaultAddr.department)
            const cost = config ? parseFloat(config.surcharge) : 0
            setShippingAddress(defaultAddr.id, cost)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addresses, shippingConfigs])

    const handleAddressSelect = (addressId: number) => {
        const addr = addresses?.find((a) => a.id === addressId)
        if (!addr) return
        const config = shippingConfigs?.find((s) => s.department === addr.department)
        const cost = config ? parseFloat(config.surcharge) : 0
        setShippingAddress(addressId, cost)
    }

    // Under the order-on-payment model the cart is the durable pre-payment
    // state — we do NOT create an order here. We just hand off to the checkout
    // page, which collects the card and calls /api/checkout/pay. The order is
    // created only when the payment confirms (resumable from any device).
    const handleProceedToPayment = () => {
        if (items.length === 0) {
            toast.error('Tu carrito está vacío')
            return
        }
        if (!shippingAddressId) {
            toast.error('Selecciona una dirección de envío')
            return
        }
        navigate(ROUTES.checkout)
    }

    // Empty state
    if (items.length === 0) {
        return (
            <div className={styles.container}>
                <h1 className={styles.pageTitle}>
                    <RiShoppingCart2Line className={styles.pageTitleIcon} />
                    Carrito de compras
                </h1>
                <div className={styles.emptyState}>
                    <RiShoppingCart2Line className={styles.emptyIcon} />
                    <h2 className={styles.emptyTitle}>Tu carrito está vacío</h2>
                    <p className={styles.emptyText}>
                        Diseña un documento y agrégalo aquí para imprimir.
                    </p>
                    <Button variant="primary" onClick={() => navigate(ROUTES.projects)}>
                        Ir a mis proyectos
                    </Button>
                </div>
            </div>
        )
    }

    const selectedAddr = addresses?.find((a) => a.id === shippingAddressId)

    return (
        <div className={styles.container}>
            <h1 className={styles.pageTitle}>
                <RiShoppingCart2Line className={styles.pageTitleIcon} />
                Carrito de compras
                <span className={styles.itemCountBadge}>{items.length} {items.length === 1 ? 'producto' : 'productos'}</span>
            </h1>

            <div className={styles.layout}>
                {/* Left: items + address + notes */}
                <div className={styles.main}>
                    {/* Items list */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>Productos</h2>
                        <div className={styles.itemsList}>
                            {items.map((item) => (
                                <CartItemRow key={item.id} item={item} />
                            ))}
                        </div>
                        <Link to={ROUTES.projects} className={styles.addMoreLink}>
                            <RiAddLine />
                            Agregar más productos
                        </Link>
                    </div>

                    {/* Shipping address */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>
                            <RiMapPinLine />
                            Dirección de envío
                        </h2>

                        {loadingAddresses ? (
                            <div className={styles.spinnerWrap}><Spinner size="sm" /></div>
                        ) : addresses && addresses.length > 0 ? (
                            <div className={styles.addressGrid}>
                                {addresses.map((addr) => (
                                    <button
                                        key={addr.id}
                                        className={`${styles.addressCard} ${shippingAddressId === addr.id ? styles.addressSelected : ''}`}
                                        onClick={() => handleAddressSelect(addr.id)}
                                    >
                                        <strong className={styles.addressLabel}>{addr.label}</strong>
                                        <span className={styles.addressLine}>{addr.address_line}</span>
                                        <span className={styles.addressMeta}>
                                            {addr.district}, {addr.province}
                                        </span>
                                        <span className={styles.addressDept}>{addr.department_display}</span>
                                        {addr.is_default && (
                                            <span className={styles.defaultBadge}>Predeterminada</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className={styles.noAddresses}>No tienes direcciones guardadas.</p>
                        )}

                        <button
                            className={styles.addAddressBtn}
                            onClick={() => openModal(<AddressFormModal />)}
                        >
                            <RiAddLine />
                            Agregar nueva dirección
                        </button>

                        {selectedAddr && (
                            <div className={styles.shippingInfo}>
                                <RiMapPinLine className={styles.shippingIcon} />
                                <span>
                                    Envío a <strong>{selectedAddr.department_display}</strong>
                                    {shippingCost > 0
                                        ? `: S/ ${shippingCost.toFixed(2)}`
                                        : ': Incluido'
                                    }
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>Notas del pedido <span className={styles.optional}>(opcional)</span></h2>
                        <textarea
                            className={styles.notesInput}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Instrucciones especiales para la impresión o entrega..."
                            rows={3}
                        />
                    </div>
                </div>

                {/* Right: summary + CTA */}
                <div className={styles.sidebar}>
                    <div className={styles.summaryCard}>
                        <h2 className={styles.summaryTitle}>Resumen del pedido</h2>

                        <div className={styles.summaryLines}>
                            {items.map((item) => (
                                <div key={item.id} className={styles.summaryLine}>
                                    <span className={styles.summaryLineName}>
                                        {item.projectName}
                                        <span className={styles.summaryLineQty}> ×{item.quantityMillares}m</span>
                                    </span>
                                    <span>S/ {item.subtotal.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div className={styles.summaryDivider} />

                        <div className={styles.summaryTotals}>
                            <div className={styles.summaryRow}>
                                <span>Subtotal</span>
                                <span>S/ {itemsTotal.toFixed(2)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span>Envío</span>
                                <span className={shippingCost === 0 ? styles.freeShipping : ''}>
                                    {shippingAddressId
                                        ? shippingCost > 0 ? `S/ ${shippingCost.toFixed(2)}` : 'Incluido'
                                        : '—'
                                    }
                                </span>
                            </div>
                            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                                <span>Total</span>
                                <span>S/ {total.toFixed(2)}</span>
                            </div>
                        </div>

                        <Button
                            variant="primary"
                            onClick={handleProceedToPayment}
                            disabled={!shippingAddressId}
                            className={styles.checkoutBtn}
                        >
                            Proceder al pago
                            <RiArrowRightLine />
                        </Button>

                        <p className={styles.secureNote}>
                            Pago seguro procesado por Mercado Pago
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
